/**
 * The learner's mother tongue, hand in hand (D-122): any help text (instruction and feedback of an
 * exercise, correction of a mock exam, AI feedback, home tips, method, vocabulary) translated by
 * the server once per language and kept. Pure logic shared by the web and the phone: reading the
 * replies, grouping the texts of a screen into one request, the « Pour vous qui parlez <langue> »
 * traps. No React and no platform API here.
 */

import { isObject, objects, str } from "./ielts/read";

export type TextSource = "fr" | "en" | "de";
export type TextKind = "help" | "instruction" | "feedback" | "correction" | "ai_feedback" | "tutor" | "home" | "method" | "glossary" | "reminder";
/** ready (translated), pending (being made), same (already in that language), unavailable (no AI),
 * limit (daily cap of new texts reached), too_long, blocked (a timed exam section is running). */
export type TextStatus = "ready" | "pending" | "same" | "unavailable" | "limit" | "too_long" | "blocked";

export interface TranslatedText {
  key: string;
  status: TextStatus;
  text: string;
  beta: boolean;
  message: string;
}

const TEXT_STATUSES: readonly TextStatus[] = ["ready", "pending", "same", "unavailable", "limit", "too_long", "blocked"];

/** The reply of `academy/mother-tongue/texts/`, one entry per text sent (same order). */
export function parseTexts(value: unknown): TranslatedText[] {
  const data = isObject(value) ? value : {};
  const beta = data.beta === true;
  const message = str(data.message);
  return objects(data.items).map((item) => ({
    key: str(item.key),
    status: TEXT_STATUSES.find((s) => s === item.status) ?? "unavailable",
    text: str(item.text),
    beta,
    message,
  }));
}

/** Translate only when a mother tongue is known and differs from the text's language. */
export function needsTranslation(motherTongue: string, source: string): boolean {
  return Boolean(motherTongue) && motherTongue !== source;
}

/** Ask again while the server is making it. */
export function textPending(value: TranslatedText | null | undefined): boolean {
  return value?.status === "pending";
}

export interface TextRequest {
  text: string;
  source: TextSource;
  kind: TextKind;
  language: string;
  examLanguage?: TextSource;
}

export interface TextBatchBody {
  language: string;
  texts: string[];
  source_language: TextSource;
  kind: TextKind;
  exam_language?: TextSource;
}

const MAX_TEXTS = 40;

/**
 * Groups the texts asked by the components of one screen into a few requests (same language,
 * source, kind and exam language; at most 40 texts each) a moment after the first one. `send`
 * posts one body and returns the server's reply; each caller gets its own text back. A refused
 * request (409: a timed exam section is running) gives « blocked »; any other failure
 * « unavailable » (the original always stays on screen).
 */
export function createTextBatcher(send: (body: TextBatchBody) => Promise<{ status: number; data: unknown }>, delayMs = 40) {
  type Waiting = { request: TextRequest; resolve: (value: TranslatedText) => void };
  let queue: Waiting[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;

  const flush = async () => {
    timer = null;
    const waiting = queue;
    queue = [];
    const groups = new Map<string, Waiting[]>();
    for (const item of waiting) {
      const r = item.request;
      const id = [r.language, r.source, r.kind, r.examLanguage ?? ""].join("|");
      groups.set(id, [...(groups.get(id) ?? []), item]);
    }
    for (const group of groups.values()) {
      for (let start = 0; start < group.length; start += MAX_TEXTS) {
        const part = group.slice(start, start + MAX_TEXTS);
        const head = part[0];
        if (!head) continue;
        const first = head.request;
        const texts = [...new Set(part.map((p) => p.request.text))];
        const body: TextBatchBody = { language: first.language, texts, source_language: first.source, kind: first.kind };
        if (first.examLanguage) body.exam_language = first.examLanguage;
        let items: TranslatedText[] = [];
        let fallback: TextStatus = "unavailable";
        try {
          const reply = await send(body);
          if (reply.status === 200) items = parseTexts(reply.data);
          else if (reply.status === 409) fallback = "blocked";
        } catch (error) {
          const status = isObject(error) && typeof error.status === "number" ? error.status : 0;
          if (status === 409) fallback = "blocked";
        }
        for (const p of part) {
          const found = items[texts.indexOf(p.request.text)];
          p.resolve(found ?? { key: "", status: fallback, text: "", beta: false, message: "" });
        }
      }
    }
  };

  return (request: TextRequest): Promise<TranslatedText> =>
    new Promise((resolve) => {
      queue.push({ request, resolve });
      if (timer === null) timer = setTimeout(() => void flush(), delayMs);
    });
}

// --- « Pour vous qui parlez <langue> » ------------------------------------------------------

export interface Pitfall {
  title: string;
  fr: string;
  en: string;
  native: string;
  example: string;
}

export interface Pitfalls {
  status: "none" | "pending" | "ready" | "failed" | "same" | "unavailable";
  language: string;
  beta: boolean;
  /** to_review (prepared by the AI, not read by a trainer yet) or approved. */
  review: string;
  intro: { fr: string; en: string; native: string };
  items: Pitfall[];
  message: string;
}

const PITFALL_STATUSES: readonly Pitfalls["status"][] = ["none", "pending", "ready", "failed", "same", "unavailable"];

export function parsePitfalls(value: unknown): Pitfalls {
  const data = isObject(value) ? value : {};
  const content = isObject(data.content) ? data.content : {};
  const intro = isObject(content.intro) ? content.intro : {};
  return {
    status: PITFALL_STATUSES.find((s) => s === data.status) ?? "none",
    language: str(data.language),
    beta: data.beta === true,
    review: str(data.review),
    intro: { fr: str(intro.fr), en: str(intro.en), native: str(intro.native) },
    items: objects(content.items)
      .map((item) => ({ title: str(item.title), fr: str(item.fr), en: str(item.en), native: str(item.native), example: str(item.example) }))
      .filter((item) => item.title),
    message: str(data.message),
  };
}

/** A trap explained in the interface language (French or English), its mother-tongue version
 * shown below it when there is one. */
export function pitfallText(item: Pitfall, interfaceLanguage: string): { main: string; native: string } {
  const main = interfaceLanguage.startsWith("en") ? item.en || item.fr : item.fr || item.en;
  return { main, native: item.native && item.native !== main ? item.native : "" };
}
